import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Container,
  Stack,
  Chip
} from '@mui/material';
import { Security, Smartphone, ArrowBack } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { supabase } from '../../supabaseClient';
import { useTheme } from '../../ThemeContext';

const TwoFactorVerification = ({ onSuccess, onBack }) => {
  const [verifyCode, setVerifyCode] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [factors, setFactors] = useState([]);
  const [selectedFactor, setSelectedFactor] = useState(null);
  const { theme } = useTheme();

  useEffect(() => {
    loadFactors();
  }, []);

  const loadFactors = async () => {
    try {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (error) throw error;
      
      const allFactors = [...data.totp, ...data.phone];
      const verifiedFactors = allFactors.filter(factor => factor.status === 'verified');
      
      setFactors(verifiedFactors);
      
      // Selecteer automatisch eerste factor als er maar één is
      if (verifiedFactors.length === 1) {
        setSelectedFactor(verifiedFactors[0]);
      }
    } catch (error) {
      console.error('Error loading factors:', error);
      setError(`Fout bij laden van authenticatie factoren: ${error.message}`);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    
    if (!selectedFactor) {
      setError('Geen authenticatie factor geselecteerd');
      return;
    }
    
    if (!verifyCode || verifyCode.length !== 6) {
      setError('Voer een geldige 6-cijferige code in');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Maak een challenge voor de geselecteerde factor
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId: selectedFactor.id
      });

      if (challengeError) throw challengeError;

      // Verificeer de code
      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId: selectedFactor.id,
        challengeId: challengeData.id,
        code: verifyCode
      });

      if (verifyError) throw verifyError;

      // Succes! Roep callback aan
      onSuccess();
      
    } catch (error) {
      console.error('2FA verification error:', error);
      if (error.message.includes('Invalid TOTP code')) {
        setError('Ongeldige code. Controleer je authenticator app en probeer opnieuw.');
      } else {
        setError(`Verificatie mislukt: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut",
        delay: 0.2
      }
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.primary.light}20 50%, ${theme.palette.primary.main}10 100%)`,
        p: 2,
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23000000" fill-opacity="0.02"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          opacity: 0.3,
        }
      }}
    >
      <Container maxWidth="xs" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={cardVariants}>
            <Paper 
              elevation={8} 
              sx={{ 
                p: { xs: 3, sm: 4 }, 
                width: '100%', 
                borderRadius: 4,
                background: (theme) => theme.palette.mode === 'dark' 
                  ? 'rgba(0, 0, 0, 0.6)' 
                  : 'rgba(255, 255, 255, 0.85)',
                backdropFilter: 'blur(20px)',
                border: (theme) => `1px solid ${theme.palette.divider}`,
                boxShadow: (theme) => `0 20px 40px -12px ${theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.8)' : 'rgba(0, 0, 0, 0.15)'}`,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                }
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
                <motion.div
                  whileHover={{ scale: 1.05, rotate: 2 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      mb: 2,
                      borderRadius: 3,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: `0 8px 20px ${theme.palette.primary.main}40`,
                    }}
                  >
                    <Security sx={{ fontSize: 32, color: 'white' }} />
                  </Box>
                </motion.div>
                <Typography variant="h5" component="h1" sx={{ 
                  fontWeight: 'bold',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 0.5
                }}>
                  Twee-Factor Verificatie
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    mt: 0.5,
                    fontWeight: 500,
                    textAlign: 'center'
                  }}>
                  Voer de code uit je authenticator app in
                </Typography>
              </Box>

              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Alert 
                    severity="error" 
                    sx={{ 
                      mb: 3, 
                      borderRadius: 2
                    }} 
                    onClose={() => setError(null)}
                  >
                    {error}
                  </Alert>
                </motion.div>
              )}

              {factors.length > 1 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Kies authenticatie methode:
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{
                    flexWrap: "wrap"
                  }}>
                    {factors.map((factor) => (
                      <Chip
                        key={factor.id}
                        icon={<Smartphone />}
                        label={factor.friendly_name || 'Authenticator App'}
                        onClick={() => setSelectedFactor(factor)}
                        color={selectedFactor?.id === factor.id ? 'primary' : 'default'}
                        variant={selectedFactor?.id === factor.id ? 'filled' : 'outlined'}
                        clickable
                      />
                    ))}
                  </Stack>
                </Box>
              )}

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
              >
                <Box component="form" onSubmit={handleVerify} sx={{ mt: 1 }}>
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    label="Verificatie Code"
                    value={verifyCode}
                    onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="123456"
                    autoFocus
                    helperText="6-cijferige code uit je authenticator app"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: `0 0 0 2px ${theme.palette.primary.main}20`,
                        },
                        '&.Mui-focused': {
                          boxShadow: `0 0 0 2px ${theme.palette.primary.main}40`,
                        }
                      }
                    }}
                    slotProps={{
                      htmlInput: {
                        maxLength: 6,
                        style: { 
                          textAlign: 'center', 
                          fontSize: '1.5rem', 
                          letterSpacing: '0.5em',
                          fontWeight: 'bold'
                        }
                      }
                    }}
                  />


                  <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                    <Button
                      variant="outlined"
                      onClick={onBack}
                      startIcon={<ArrowBack />}
                      sx={{ 
                        flex: 1,
                        py: 1.5,
                        borderRadius: 3
                      }}
                    >
                      Terug
                    </Button>
                    <Button
                      variant="contained"
                      type="submit"
                      disabled={loading || verifyCode.length !== 6}
                      sx={{ 
                        flex: 2,
                        py: 1.5,
                        borderRadius: 3,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                        boxShadow: `0 4px 12px ${theme.palette.primary.main}40`,
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        '&:hover': {
                          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                          boxShadow: `0 6px 20px ${theme.palette.primary.main}60`,
                        }
                      }}
                    >
                      {loading ? <CircularProgress size={24} color="inherit" /> : "Verifiëren"}
                    </Button>
                  </Stack>
                </Box>
              </motion.div>
            </Paper>
          </motion.div>
        </motion.div>
      </Container>
    </Box>
  );
};

export default TwoFactorVerification;

